import { objectValues, satisfiesKey, satisfiesRecord } from "@nens/shared-utils";

import { AppState } from "../App";

import { BACK_TO_START_SCREEN, BackToStartScreen } from "./appState";

export const OBJECT_TYPES: string[] = [
  "breaches", // breach is always from 1d to 2d
  "channels", // sloten. altijd tussen 2 connection nodes. Worden added calculation lines aan toegevoegt.
  "culverts", // duiker tussen 2 connection nodes
  "levees",
  "manholes", // put , of connection node met het oppervlak
  "nodes", // centerpunt van cellen / onnodig ?
  "orifices",
  "pipes", // ondergrondse pijp, sewage bestaat uit pijpen
  "pumps", // tussen 2 connection nodes. willen we eigelijk wel een lijntje.  dit kan uit. Nici zegt per pomp aan of uit
  "weirs", // stuw tussen 2 connection nodes. Liggen altijd dicht bij elkaar.
];

export const OTHER_PIPE_TYPE_KEY = "other" as const;

export const PIPE_TYPES = satisfiesKey<string | number>()({
  0: "mixedwater",
  1: "rainwater",
  2: "wastewater",
  3: "transportwater",
  4: "spillwaywater",
  5: "syphonwater",
  6: "storagewater",
  7: "storageandsettlementtankwater",
  [OTHER_PIPE_TYPE_KEY]: "otherwater",
} as const);

export type PipeType = (typeof PIPE_TYPES)[keyof typeof PIPE_TYPES];

export interface OneDLayerConfigDefault {
  layerName: string;
  visible: boolean;
  color: string;
}

export interface OneDLayerConfigPipe extends OneDLayerConfigDefault {
  pipeStyles: Record<PipeType, PipeConfig>;
}

export interface PipeConfig {
  layerName: string;
  visible: boolean;
  color: string;
  dashed?: boolean;
}

const INITIAL_STATE = {
  layers: {
    ...satisfiesRecord<OneDLayerConfigDefault>()({
      "added calculation node": {
        layerName: "Added calculation node",
        visible: true,
        color: "#DEB887",
      },
      breaches: {
        layerName: "Breaches",
        visible: true,
        color: "#b88756",
      },
      channels: {
        layerName: "Channels",
        visible: true,
        color: "#054dd1",
      },
      culverts: {
        layerName: "Culverts",
        visible: true,
        color: "#FF7F50",
      },
      levees: {
        layerName: "Levees",
        visible: true,
        color: "#656565",
      },
      manholes: {
        layerName: "Manholes",
        visible: true,
        color: "#000000", // currently not used, but used later on
      },
      nodes: {
        layerName: "Nodes",
        visible: true,
        color: "#000000",
      },
      orifices: {
        layerName: "Orifices",
        visible: true,
        color: "#33A02C",
      },
      pumps: {
        layerName: "Pump stations",
        visible: true,
        color: "#ffaa00",
      },
      weirs: {
        layerName: "Weirs",
        visible: true,
        color: "#d7191c",
      },
    }),
    ...satisfiesRecord<OneDLayerConfigPipe>()({
      pipes: {
        layerName: "Sewers",
        visible: true,
        color: "#ffaa00",
        pipeStyles: {
          rainwater: {
            layerName: "Rain water",
            visible: true,
            color: "#55aaff",
          },
          wastewater: {
            layerName: "Waste water",
            visible: true,
            color: "#ff0000",
          },
          mixedwater: {
            layerName: "Mixed",
            visible: true,
            color: "#ffaa00",
          },
          transportwater: {
            layerName: "Transport",
            visible: true,
            color: "#808080",
          },
          spillwaywater: {
            layerName: "Spillway",
            visible: true,
            color: "#5FAFFF",
            dashed: true,
          },
          syphonwater: {
            layerName: "Syphon",
            visible: true,
            color: "#5FAFFF",
          },
          storagewater: {
            layerName: "Storage",
            visible: true,
            color: "#BDBDBD",
          },
          storageandsettlementtankwater: {
            layerName: "Storage and settlement tank",
            visible: true,
            color: "#5C5C5C",
          },
          otherwater: {
            layerName: "Other",
            visible: true,
            color: "#0F0F0F",
            dashed: true,
          },
        },
      },
    }),
  },
};

export type LayerOneDMenu = typeof INITIAL_STATE;
export type Layers = LayerOneDMenu["layers"];
export type OneDLayerConfig = Layers[keyof Layers];
export type LayerType = keyof Layers;

export const getLayers = (state: AppState) => (state.layerOneDMenu as LayerOneDMenu).layers;

const SET_VISIBILITY = "layerOneDMenu/SET_VISIBILITY";

interface SetVisibility {
  type: typeof SET_VISIBILITY;
  layer: LayerType;
  visible: boolean;
}

export const setVisibility = (layer: LayerType, visible: boolean) => {
  return {
    type: SET_VISIBILITY,
    layer,
    visible,
  };
};

const SET_PIPES_VISIBILITY = "layerOneDMenu/SET_PIPES_VISIBILITY";

interface SetPipesVisibility {
  type: typeof SET_PIPES_VISIBILITY;
  layer: PipeType;
  visible: boolean;
}

export const setPipesVisibility = (layer: PipeType, visible: boolean) => {
  return {
    type: SET_PIPES_VISIBILITY,
    layer,
    visible,
  };
};

const SET_ALL_SEWER_PIPES_VISIBLE = "layerOneDMenu/SET_ALL_SEWER_PIPES_VISIBLE";

interface SetAllSewerPipesVisible {
  type: typeof SET_ALL_SEWER_PIPES_VISIBLE;
  visible: boolean;
}

export const setAllSewerPipesVisible = (visible: boolean) => {
  return {
    type: SET_ALL_SEWER_PIPES_VISIBLE,
    visible: visible,
  };
};

const SET_COLOR = "layerOneDMenu/SET_COLOR";

interface SetColor {
  type: typeof SET_COLOR;
  layer: LayerType;
  color: string;
}

export const setColor = (layer: LayerType, color: string) => {
  return {
    type: SET_COLOR,
    layer,
    color,
  };
};

const SET_COLOR_PIPE = "layerOneDMenu/SET_COLOR_PIPE";

interface SetColorPipe {
  type: typeof SET_COLOR_PIPE;
  layer: PipeType;
  color: string;
}

export const setColorPipe = (layer: PipeType, color: string) => {
  return {
    type: SET_COLOR_PIPE,
    layer,
    color,
  };
};

type LayerOneDMenuAction =
  | SetVisibility
  | SetPipesVisibility
  | SetAllSewerPipesVisible
  | SetColor
  | SetColorPipe
  | BackToStartScreen;

export default function reducer(state = INITIAL_STATE, action: LayerOneDMenuAction) {
  switch (action.type) {
    case BACK_TO_START_SCREEN:
      return INITIAL_STATE;
    case SET_VISIBILITY: {
      const { layer, visible } = action;
      if (!state.layers[layer]) return state;
      return {
        ...state,
        layers: {
          ...state.layers,
          [layer]: {
            ...state.layers[layer],
            visible,
          },
        },
      };
    }
    case SET_PIPES_VISIBILITY: {
      const { layer, visible } = action;
      if (
        !state.layers["pipes"] ||
        !state.layers["pipes"].pipeStyles ||
        !state.layers["pipes"].pipeStyles[layer]
      )
        return state;
      return {
        ...state,
        layers: {
          ...state.layers,
          pipes: {
            ...state.layers.pipes,
            pipeStyles: {
              ...state.layers.pipes.pipeStyles,
              [layer]: {
                ...state.layers.pipes.pipeStyles[layer],
                visible,
              },
            },
          },
        },
      };
    }
    case SET_ALL_SEWER_PIPES_VISIBLE: {
      const { visible } = action;
      if (!state.layers["pipes"] || !state.layers["pipes"].pipeStyles) {
        return state;
      }
      const newPipeStyles: { [pipeType: string]: PipeConfig } = {};
      const keysPipeStylesKeys = objectValues(PIPE_TYPES);
      keysPipeStylesKeys.forEach((k) => {
        if (state.layers["pipes"].pipeStyles && state.layers["pipes"].pipeStyles[k]) {
          newPipeStyles[k] = { ...state.layers["pipes"].pipeStyles[k], visible: visible };
        }
      });
      return {
        ...state,
        layers: {
          ...state.layers,
          pipes: {
            ...state.layers.pipes,
            pipeStyles: newPipeStyles,
          },
        },
      };
    }
    case SET_COLOR: {
      const { layer, color } = action;
      if (!state.layers[layer]) return state;
      return {
        ...state,
        layers: {
          ...state.layers,
          [layer]: {
            ...state.layers[layer],
            color,
          },
        },
      };
    }
    case SET_COLOR_PIPE: {
      const { layer, color } = action;
      if (
        !state.layers["pipes"] ||
        !state.layers["pipes"].pipeStyles ||
        !state.layers["pipes"].pipeStyles[layer]
      )
        return state;
      return {
        ...state,
        layers: {
          ...state.layers,
          pipes: {
            ...state.layers.pipes,
            pipeStyles: {
              ...state.layers.pipes.pipeStyles,
              [layer]: {
                ...state.layers.pipes.pipeStyles[layer],
                color,
              },
            },
          },
        },
      };
    }
    default:
      return state;
  }
}
